import React, { useEffect, useState, useCallback } from "react";
import { useParams, useHistory, Link } from "react-router-dom";
import ajaxService from "../../../services/ajax-service";
import moment from "moment";
import { calculateAvailableWidth, calculateModalWidth } from "../../../utils/widthCalculator";
import {
  Button,
  Card,
  Popconfirm,
  message,
  Space,
  Breadcrumb,
  Tag,
  Descriptions,
  Spin,
  Modal,
  Form,
  Input,
  Row,
  Col,
  Select,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  HomeOutlined,
  DeleteFilled,
} from "@ant-design/icons";

// Small reusable component for rendering options
const QuestionOptions = ({ options }) => {
  if (!options || options.length === 0) {
    return <span style={{ color: 'var(--text-gray)' }}>No options available</span>;
  }
  return (
    <ul style={{ listStyle: 'disc', paddingLeft: 'var(--spacing-xl)' }}>
      {options.map((option) => (
        <li key={option.id} style={{ color: 'var(--text-white)', marginBottom: 'var(--spacing-xs)' }}>{option.option}</li>
      ))}
    </ul>
  );
};

const QuestionDetails = () => {
  const { id } = useParams();
  const history = useHistory();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [cardWidth, setCardWidth] = useState('100%');
  const [modalWidth, setModalWidth] = useState('35%');

  const [showEditModal, setShowEditModal] = useState(false);
  const [form] = Form.useForm();
  const [options, setOptions] = useState([{ id: 1, option: "" }]);
  const [questionType, setQuestionType] = useState(2);

  const fetchData = useCallback(async (id) => {
    setLoading(true);
    try {
      const response = await ajaxService.get(`/questions/${id}`);
      if (response.success) {
        setData(response.data);
        setQuestionType(response.data.options_type || 2);
        if (response.data.options_type === 1) {
          setOptions(response.data.question_options || [{ id: 1, option: "" }]);
        }
      } else {
        setError("Failed to fetch data");
      }
    } catch (err) {
      setError("Error fetching data");
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDelete = useCallback(async () => {
    try {
      const response = await ajaxService.delete("/questions/", id);
      if (response.success) {
        message.success("Question deleted successfully");
        history.push("/questions");
      } else {
        message.error("Failed to delete question");
      }
    } catch (err) {
      console.error("Delete error:", err);
      message.error("Error deleting question");
    }
  }, [id, history]);

  const openEditModal = () => {
    form.setFieldsValue({
      question: data.question,
      type: data.options_type,
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (values) => {
    const payload = {
      question: values.question,
      options_type: questionType,
      ...(questionType === 1 && {
        question_options: options.filter((opt) => opt.option.trim() !== ""),
      }),
    };

    try {
      const response = await ajaxService.put("/questions/", id, payload);
      if (response.success) {
        message.success("Question updated successfully");
        setData(response.data); // update details instantly
        fetchData(id);
        setShowEditModal(false);
      } else {
        message.error("Failed to update question");
      }
    } catch (err) {
      console.error("Update error:", err);
      message.error("Error updating question");
    }
  };

  const handleAddOption = () => {
    setOptions([...options, { id: options.length + 1, option: "" }]);
  };

  const handleOptionChange = (value, index) => {
    const updated = [...options];
    updated[index].option = value;
    setOptions(updated);
  };

  const handleRemoveOption = (index) => {
    const updated = options.filter((_, i) => i !== index);
    setOptions(updated);
  };

  useEffect(() => {
    if (id) fetchData(id);
  }, [id, fetchData]);

  // Listen for sidebar collapse state and calculate card width
  useEffect(() => {
    const updateWidth = () => {
      const width = calculateAvailableWidth(sidebarCollapsed, 40);
      setCardWidth(width);
      setModalWidth(calculateModalWidth('35%'));
    };
    
    const handleSidebarCollapse = (event) => {
      setSidebarCollapsed(event.detail.collapsed);
      updateWidth();
    };
    
    window.addEventListener('sidebarCollapse', handleSidebarCollapse);
    window.addEventListener('resize', updateWidth);
    updateWidth();
    
    return () => {
      window.removeEventListener('sidebarCollapse', handleSidebarCollapse);
      window.removeEventListener('resize', updateWidth);
    };
  }, [sidebarCollapsed]);

  if (loading) {
    return (
      <div className="page-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', padding: 'var(--spacing-2xl)' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) return (
    <div className="page-container" style={{ padding: 'var(--spacing-xl)', textAlign: 'center' }}>
      <p style={{ color: 'var(--error-red)' }}>{error}</p>
    </div>
  );
  if (!data) return (
    <div className="page-container" style={{ padding: 'var(--spacing-xl)', textAlign: 'center' }}>
      <p style={{ color: 'var(--text-gray)' }}>No data found</p>
    </div>
  );

  return (
    <div className={`page-container ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {/* Header Section */}
      <div className="module-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-lg)' }}>
          <Breadcrumb
            items={[
              {
                title: (
                  <Link to="/dashboard" style={{ color: 'var(--text-white)' }}>
                    <HomeOutlined /> Home
                  </Link>
                ),
              },
              { title: <Link to="/questions" style={{ color: 'var(--text-white)' }}>Questions</Link> },
              {
                title: <span style={{ color: 'var(--text-gray)' }}>{data?.question ? `${data.question.substring(0, 20)}...` : "Details"}</span>,
              },
            ]}
          />
          <Space>
            <Button
              type="primary"
              ghost
              icon={<EditOutlined />}
              onClick={openEditModal}
              style={{
                background: 'var(--primary-purple)',
                borderColor: 'var(--primary-purple)',
                color: 'white',
              }}
            >
              Edit
            </Button>

            <Popconfirm
              title="Are you sure to delete this question?"
              onConfirm={handleDelete}
              okText="Yes"
              cancelText="No"
            >
              <Button 
                danger 
                icon={<DeleteOutlined />}
                style={{
                  background: 'var(--error-red)',
                  borderColor: 'var(--error-red)',
                }}
              >
                Delete
              </Button>
            </Popconfirm>

            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => history.push("/questions")}
              style={{
                background: 'var(--dark-bg-card)',
                borderColor: 'rgba(254, 2, 125, 0.3)',
                color: 'var(--text-white)',
              }}
            >
              Back
            </Button>
          </Space>
        </div>
        <h1 className="module-title">Question Details</h1>
        <p className="module-subtitle">View and manage question information</p>
      </div>

      <Card 
        style={{
          width: typeof cardWidth === 'string' ? cardWidth : `${cardWidth}px`,
          maxWidth: '100%',
          boxShadow: 'var(--shadow-card)',
          borderRadius: 'var(--radius-lg)',
          background: 'var(--dark-bg-card)',
          border: '1px solid rgba(254, 2, 125, 0.2)',
        }}
      >
        {/* Details */}
        <Descriptions
          bordered
          column={1}
          labelStyle={{ fontWeight: "bold", width: "200px", color: 'var(--text-white)' }}
        >
          <Descriptions.Item label="ID"><span style={{ color: 'var(--text-white)' }}>{data.id}</span></Descriptions.Item>
          <Descriptions.Item label="Question">
            <span style={{ color: 'var(--text-white)' }}>{data.question || "N/A"}</span>
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={data.status === 1 ? "green" : "red"}>
              {data.status === 1 ? "Active" : "Inactive"}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Created At">
            <span style={{ color: 'var(--text-white)' }}>
              {data.created_at
                ? moment(data.created_at).format("YYYY-MM-DD HH:mm:ss")
                : "N/A"}
            </span>
          </Descriptions.Item>
          <Descriptions.Item label="Updated At">
            <span style={{ color: 'var(--text-white)' }}>
              {data.updated_at
                ? moment(data.updated_at).format("YYYY-MM-DD HH:mm:ss")
                : "N/A"}
            </span>
          </Descriptions.Item>
          <Descriptions.Item label="Options">
            <QuestionOptions options={data.question_options} />
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Edit Modal */}
      <Modal
        title={<span style={{ color: 'var(--text-white)' }}>Edit Question</span>}
        open={showEditModal}
        onCancel={() => setShowEditModal(false)}
        footer={null}
        className="modal-overlay"
        width={modalWidth}
        centered
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleEditSubmit}
          initialValues={{ type: 2 }}
        >
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label={<span style={{ color: 'var(--text-white)' }}>Question Type</span>}
                name="type"
                rules={[{ required: true, message: "Select question type" }]}
              >
                <Select
                  className="input-field"
                  placeholder="Select question type"
                  onChange={(value) => setQuestionType(Number(value))}
                >
                  <Select.Option value={2}>Simple Question</Select.Option>
                  <Select.Option value={1}>Question With Options</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label={<span style={{ color: 'var(--text-white)' }}>Question</span>}
                name="question"
                rules={[{ required: true, message: "Enter question" }]}
              >
                <Input 
                  placeholder="Enter question" 
                  className="input-field"
                />
              </Form.Item>
            </Col>
          </Row>

          {questionType === 1 && (
            <>
              <h4 style={{ marginTop: 'var(--spacing-sm)', color: 'var(--text-white)' }}>Options</h4>
              {options.map((opt, idx) => (
                <Row key={idx} gutter={8} style={{ marginBottom: 8 }}>
                  <Col span={20}>
                    <Input
                      value={opt.option}
                      placeholder={`Option ${idx + 1}`}
                      onChange={(e) => handleOptionChange(e.target.value, idx)}
                      className="input-field"
                    />
                  </Col>
                  <Col span={4}>
                    {options.length > 1 && (
                      <Button 
                        danger 
                        onClick={() => handleRemoveOption(idx)}
                        style={{
                          background: 'var(--error-red)',
                          borderColor: 'var(--error-red)',
                          width: '100%'
                        }}
                      >
                        <DeleteFilled />
                      </Button>
                    )}
                  </Col>
                </Row>
              ))}
              <Button 
                block 
                onClick={handleAddOption} 
                type="dashed"
                style={{
                  borderColor: 'rgba(254, 2, 125, 0.3)',
                  color: 'var(--text-white)',
                }}
              >
                Add Option
              </Button>
            </>
          )}

          <div style={{ textAlign: "center", marginTop: 20 }}>
            <Button 
              type="primary" 
              htmlType="submit"
              style={{
                background: 'var(--primary-purple)',
                borderColor: 'var(--primary-purple)',
              }}
            >
              Update
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default QuestionDetails;
